import React, { useState, useCallback } from 'react';
import { Grid, useTheme, useMediaQuery, Card, CardContent } from '@mui/material';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import Button from '@mui/material/Button';
import OutlinedInput from '@mui/material/OutlinedInput';
import { Typography } from '@mui/material';
import Alert from '@mui/material/Alert';
import { useNavigate } from "react-router-dom";

import api from '../../utils/webcall';
import { VisuallyHiddenInput, transformUnits, getErrorMessage } from '../../utils/utils';
import { DEFAULT_MEASUREMENT_UNIT } from '../../utils/constants';
import UserProfile from '../../components/User';
import AccessController from '../../components/access_controller';
import MeasurementUnitSelect from '../../components/commons/MeasurementUnitSelect';


function BillDataTable(props) {
    const { data, update, errors } = props;  // Add errors prop
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    if (data.length === 0) {
        return <></>
    }

    if (isMobile) {
        return (
            <Grid container spacing={2}>
                {data.map((item, index) => (
                    <Grid size={{ xs: 12 }} key={`bill-data-card-${index}`}>
                        <Card variant="outlined">
                            <CardContent>
                                <Grid container spacing={2}>
                                    <Grid size={{ xs: 12 }}>
                                        <OutlinedInput
                                            required
                                            fullWidth
                                            placeholder="Item Name"
                                            value={item['item_name']}
                                            onChange={(e) => update(index, e.target.value, 'item_name')}
                                            error={errors?.[index]?.['item_name']}
                                            sx={{ '& input': { fontSize: '1.1rem', py: 1.5 } }}
                                        />
                                        {errors?.[index]?.['item_name'] && (
                                            <Typography color="error" variant="caption" sx={{ ml: 1 }}>
                                                Item name is required
                                            </Typography>
                                        )}
                                    </Grid>
                                    <Grid size={{ xs: 12, sm: 6 }}>
                                        <OutlinedInput
                                            required
                                            fullWidth
                                            placeholder="Quantity"
                                            value={item['quantity']}
                                            onChange={(e) => update(index, e.target.value, 'quantity')}
                                            error={errors?.[index]?.['quantity']}
                                            type="number"
                                            inputProps={{ 
                                                inputMode: 'decimal',
                                                min: "0.01",
                                                step: "0.01"
                                            }}
                                            sx={{ '& input': { fontSize: '1.1rem', py: 1.5 } }}
                                        />
                                        {errors?.[index]?.['quantity'] && (
                                            <Typography color="error" variant="caption" sx={{ ml: 1 }}>
                                                Quantity is required and must be > 0
                                            </Typography>
                                        )}
                                    </Grid>
                                    <Grid size={{ xs: 12, sm: 6 }}>
                                        <MeasurementUnitSelect
                                            fullWidth
                                            required
                                            value={item['measurement_unit']}
                                            onChange={(e) => update(index, e.target.value, 'measurement_unit')}
                                            sx={{ minHeight: 54 }}
                                        />
                                    </Grid>
                                    <Grid size={{ xs: 12 }}>
                                        <OutlinedInput
                                            fullWidth
                                            required={true}
                                            placeholder="Total Price"
                                            value={item['total_price']}
                                            onChange={(e) => update(index, e.target.value, 'total_price')}
                                            type="number"
                                            inputProps={{ inputMode: 'decimal' }}
                                            sx={{ '& input': { fontSize: '1.1rem', py: 1.5 } }}
                                        />
                                    </Grid>
                                    {item['quantity'] && item['total_price'] && (
                                        <Grid size={{ xs: 12 }}>
                                            <Typography color="text.secondary">
                                                Price per unit: {(parseFloat(item['total_price']) / parseFloat(item['quantity'])).toFixed(2)}
                                            </Typography>
                                        </Grid>
                                    )}
                                </Grid>
                            </CardContent>
                        </Card>
                    </Grid>
                ))}
            </Grid>
        );
    }

    // Desktop view
    return (<>
        {data.map((item, index) => (
            <TableRow key={`bill-data-row-${index}`}>
                <TableCell align="center">
                    <OutlinedInput
                        required
                        error={errors?.[index]?.['item_name']}
                        value={item['item_name']}
                        onChange={(e) => update(index, e.target.value, 'item_name')}
                    />
                    {errors?.[index]?.['item_name'] && (
                        <Typography color="error" variant="caption" display="block">
                            Required
                        </Typography>
                    )}
                </TableCell>
                <TableCell align="center">
                    <OutlinedInput
                        required
                        error={errors?.[index]?.['quantity']}
                        value={item['quantity']}
                        onChange={(e) => update(index, e.target.value, 'quantity')}
                    />
                    {errors?.[index]?.['quantity'] && (
                        <Typography color="error" variant="caption" display="block">
                            Required
                        </Typography>
                    )}
                </TableCell>
                <TableCell align="center">
                    <MeasurementUnitSelect
                        value={item['measurement_unit']}
                        onChange={(e) => {
                            update(index, e.target.value, 'measurement_unit');
                        }}
                    />
                </TableCell>
                <TableCell align="center">
                    <OutlinedInput
                        error={errors?.[index]?.['total_price']}
                        value={item['total_price']}
                        onChange={(e) => update(index, e.target.value, 'total_price')}
                    />
                    {errors?.[index]?.['total_price'] && (
                        <Typography color="error" variant="caption" display="block">
                            Required
                        </Typography>
                    )}
                </TableCell>
                <TableCell align="center">{parseFloat(item['total_price']) / parseFloat(item['quantity'])}</TableCell>
            </TableRow>
        ))}
    </>)
}


function AddBill(props) {
    const { showErrorMessage } = props;
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const [billData, setBillData] = useState([]);
    const [deactivateSubmit, setDeactivateSubmit] = useState(false);
    const navigate = useNavigate();
    const [selectedFile, setSelectedFile] = useState(null);
    const username = UserProfile.getName();
    const [errors, setErrors] = useState({});

    const validateForm = useCallback(() => {
        const newErrors = {};
        let isValid = true;

        billData.forEach((item, index) => {
            newErrors[index] = {};
            
            // Check item name
            if (!item.item_name?.trim()) {
                newErrors[index].item_name = true;
                isValid = false;
            }

            // Check quantity
            const qty = parseFloat(item.quantity);
            if (!item.quantity || isNaN(qty) || qty <= 0) {
                newErrors[index].quantity = true;
                isValid = false;
            }

            // Check measurement unit
            if (!item.measurement_unit) {
                newErrors[index].measurement_unit = true;
                isValid = false;
            }

            // Check price
            const price = parseFloat(item.total_price);
            if (!item.total_price || isNaN(price) || price <= 0) {
                newErrors[index].total_price = true;
                isValid = false;
            }
        });

        setErrors(newErrors);
        return isValid;
    }, [billData]);

    const handleSubmit = async () => {
        if (!validateForm()) {
            showErrorMessage('Please fill all required fields correctly');
            setDeactivateSubmit(false);
            return;
        }

        setDeactivateSubmit(true);

        const payload = billData.map((row) => {
            const [quantity, measurement_unit] = transformUnits(row['quantity'], row['measurement_unit']);
            return { ...row, quantity, measurement_unit };
        });
        let data = {
            'data': payload,
            'files': []
        }
        const form_data = new FormData();
        Array.from(selectedFile).forEach(file => {
            form_data.append(file.name, file);
        });
        data['files'] = Array.from(selectedFile).forEach(file => {
            return file.name;
        });
        form_data.append('payload', JSON.stringify(data));
        api.post(`/pos/bill/add/`, form_data).then((res) => {
            let data = res.data;
            let bill_id = data['id']
            navigate(`/bill/view/${bill_id}`)
        }).catch((error) => {
            showErrorMessage(getErrorMessage(error));
        }).finally(() => {
            setDeactivateSubmit(false);
        });

    }

    const update = (index, value, target) => {
        const temp = [...billData];  // Create new array reference
        temp[index] = { ...temp[index], [target]: value };  // Create new object reference
        
        // Clear error for this field when user types
        setErrors(prev => ({
            ...prev,
            [index]: {
                ...prev[index],
                [target]: false
            }
        }));
        
        setBillData(temp);
    }

    const addNewRow = () => {
        setBillData((prev) => [
            ...prev,
            {
                'item_name': '',
                'quantity': '',
                'measurement_unit': DEFAULT_MEASUREMENT_UNIT,
                'total_price': ''
            }
        ]);
    }

    return (
        <AccessController
            onlyAdmin={false}
        >
            <Grid
                container
                direction="column"
                spacing={isMobile ? 3 : 2}
                sx={{ p: isMobile ? 2 : 0 }}
            >
                <Grid>
                    <Typography variant="h5" align="center">DIVASH | ADMIN</Typography>
                </Grid>
                
                <Grid>
                    <Typography variant="subtitle1" align="center">
                        Welcome {username}
                    </Typography>
                    <Typography variant="h6" align="center" gutterBottom>
                        Submit New Bill
                    </Typography>
                </Grid>

                <Grid sx={{ width: '100%' }}>
                    {isMobile ? (
                        <BillDataTable data={billData} update={update} errors={errors} />
                    ) : (
                        <TableContainer component={Paper}>
                            <Table stickyHeader>
                                <TableHead>
                                    <TableRow>
                                        <TableCell align="center">Item Name</TableCell>
                                        <TableCell align="center">Quantity</TableCell>
                                        <TableCell align="center">Measurement Unit</TableCell>
                                        <TableCell align="center">Net Price</TableCell>
                                        <TableCell align="center">Price per Unit</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    <BillDataTable data={billData} update={update} errors={errors} />
                                </TableBody>
                            </Table>
                        </TableContainer>
                    )}
                </Grid>

                <Grid container spacing={2} justifyContent="center">
                    <Grid size={{ xs: 12, sm: "auto" }}>
                        <Button
                            component="label"
                            variant="contained"
                            fullWidth={isMobile}
                            size={isMobile ? "large" : "medium"}
                            sx={{ minHeight: isMobile ? 54 : 'inherit' }}
                        >
                            Upload files
                            <VisuallyHiddenInput
                                type="file"
                                onChange={(event) => setSelectedFile(event.target.files)}
                                multiple
                            />
                        </Button>
                    </Grid>
                    <Grid size={{ xs: 12, sm: "auto" }}>
                        <Button
                            variant="contained"
                            onClick={addNewRow}
                            fullWidth={isMobile}
                            size={isMobile ? "large" : "medium"}
                            sx={{ minHeight: isMobile ? 54 : 'inherit' }}
                        >
                            Add New Row
                        </Button>
                    </Grid>
                </Grid>

                {selectedFile === null && (
                    <Grid size={{ xs: 12 }}>
                        <Alert severity="error">Select Bill Image to Upload</Alert>
                    </Grid>
                )}

                {selectedFile && (
                    <Grid container spacing={1}>
                        {Array.from(selectedFile).map((item, index) => (
                            <Grid size={{ xs: 12 }} key={`file-${index}`}>
                                <Alert severity="success">{item.name}</Alert>
                            </Grid>
                        ))}
                    </Grid>
                )}

                <Grid>
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={(e) => {
                            e.preventDefault();
                            setDeactivateSubmit(true);
                            handleSubmit();
                        }}
                        disabled={!selectedFile || deactivateSubmit}
                        fullWidth={isMobile}
                        size={isMobile ? "large" : "medium"}
                        sx={{ 
                            minHeight: isMobile ? 54 : 'inherit',
                            mt: 2
                        }}
                    >
                        {deactivateSubmit ? 'Submitting...' : 'Submit'}
                    </Button>
                </Grid>
            </Grid>
        </AccessController>
    );
}

export default AddBill;